/**
 * Subscriber custom fields — shared types and helpers.
 *
 * Storage model:
 * - Definitions: `subscriber_field_definitions` (schema catalog)
 * - Values: `subscriber_field_values` (source of truth per subscriber × field)
 * - Read cache: `subscribers.custom_data` jsonb (rebuilt by DB trigger)
 *
 * Segmentation: use `channels` for boolean audience tags (GIN-indexed, newsletter
 * filter RPC). Use `custom_data` for rich attributes; SQL audience rules on
 * custom_data can be added later via GIN or joins on subscriber_field_values.
 */

export type SubscriberFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'select'
  | 'multi_select'
  | 'date'
  | 'url';

/**
 * Browser → R2 cap for the CDN page and subscriber field uploads.
 * cdn-assets body uploads (base64 / multipart) stay at 5MB.
 */
export const CDN_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

export type SubscriberFieldUi = {
  show_on_detail?: boolean;
  admin_only?: boolean;
  required?: boolean;
  help_text?: string;
  sort_order?: number;
  placeholder?: string;
  /** Admin catalog only — groups field definitions in the engine UI (not public). */
  admin_group?: string;
  /**
   * Freeform labels for workflows (e.g. Fractional Pulse: `fractional-pulse`, `week-3`).
   * Normalized to lowercase kebab / week-N.
   */
  tags?: string[];
  /**
   * On `url` fields: show a CDN upload control in the engine admin.
   * The uploaded file's public URL is stored as the field value (same as manual paste).
   */
  enable_upload?: boolean;
  /** HTML `accept` for the file picker, e.g. `image/*` or `image/jpeg,image/png`. */
  upload_accept?: string;
  /** Max upload size in bytes (capped at CDN_UPLOAD_MAX_BYTES). */
  upload_max_bytes?: number;
  /** R2 object key template; `{subscriber_id}` is replaced. Extension comes from the file. */
  upload_cdn_key_template?: string;
};

/** Program tag for Fractional Pulse survey UI. */
export const FRACTIONAL_PULSE_TAG = 'fractional-pulse';

/** Pulse fields stored on the profile only — never charted in the peer report. */
export const PROFILE_ONLY_FIELD_TAG = 'profile-only';

const WEEK_TAG_RE = /^week-(\d+)$/;
const TAG_NORMALIZE_RE = /[^a-z0-9_-]+/g;

/** Normalize a single tag to lowercase kebab (or keep `week-N`). */
export function normalizeFieldTag(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(TAG_NORMALIZE_RE, '')
    .replace(/^-+|-+$/g, '');
}

export function parseFieldTags(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const t = normalizeFieldTag(item);
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

export function fieldHasTag(ui: SubscriberFieldUi | undefined, tag: string): boolean {
  const want = normalizeFieldTag(tag);
  if (!want) return false;
  return (ui?.tags ?? []).some((t) => t === want);
}

/** Parse `week-N` from tags; returns null if none. */
export function fieldPulseWeek(ui: SubscriberFieldUi | undefined): number | null {
  for (const t of ui?.tags ?? []) {
    const m = WEEK_TAG_RE.exec(t);
    if (!m) continue;
    const n = Number(m[1]);
    if (Number.isInteger(n) && n >= 1 && n <= 12) return n;
  }
  return null;
}

/** Fields without `ui.admin_group` appear under this heading in the definitions admin. */
export const DEFAULT_ADMIN_FIELD_GROUP = 'Ungrouped';

/** Suggested group names for the definitions editor (free text still allowed). */
export const SUGGESTED_ADMIN_FIELD_GROUPS = [
  'Subscribe onboarding',
  'Profile & CRM',
  'Integrations',
  'Scoring',
  'Fractional Pulse',
] as const;

/** Suggested tags for the definitions editor (free text still allowed). */
export const SUGGESTED_FIELD_TAGS = [
  'fractional-pulse',
  'profile-only',
  'week-1',
  'week-2',
  'week-3',
  'week-4',
  'week-5',
  'week-6',
  'week-7',
  'week-8',
  'week-9',
  'week-10',
  'week-11',
  'week-12',
] as const;

export type SubscriberFieldDefinitionRow = {
  id: string;
  key: string;
  label: string;
  field_type: SubscriberFieldType;
  options: string[];
  default_value: unknown;
  ui: SubscriberFieldUi;
  active: boolean;
  score_points: number;
  extraction_prompt: string | null;
  created_at: string;
  updated_at: string;
};

export type SubscriberFieldValueRow = {
  subscriber_id: string;
  field_id: string;
  value: unknown;
  updated_at: string;
};

export type CustomDataMap = Record<string, unknown>;

export const SUBSCRIBER_FIELD_TYPES: SubscriberFieldType[] = [
  'text',
  'textarea',
  'number',
  'boolean',
  'select',
  'multi_select',
  'date',
  'url',
];

const FIELD_KEY_RE = /^[a-z][a-z0-9_]*$/;

export function normalizeFieldKey(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function isValidFieldKey(key: string): boolean {
  return FIELD_KEY_RE.test(key) && key.length >= 2 && key.length <= 64;
}

/** Format select options for the admin editor (JSON array — commas inside values are OK). */
export function formatOptionsForEditor(options: string[]): string {
  if (!options.length) return '';
  return JSON.stringify(options, null, 2);
}

export function parseOptions(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((o) => String(o).trim()).filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    const t = raw.trim();
    if (t.startsWith('[')) {
      try {
        const parsed = JSON.parse(t) as unknown;
        if (Array.isArray(parsed)) {
          return parsed.map((o) => String(o).trim()).filter(Boolean);
        }
      } catch {
        /* invalid JSON — don't fall back to comma split */
        return [];
      }
    }
    return t.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

export function mapDefinitionRow(raw: Record<string, unknown>): SubscriberFieldDefinitionRow {
  return {
    id: String(raw.id),
    key: String(raw.key),
    label: String(raw.label),
    field_type: raw.field_type as SubscriberFieldType,
    options: parseOptions(raw.options),
    default_value: raw.default_value,
    ui: parseUi(raw.ui),
    active: raw.active !== false,
    score_points:
      typeof raw.score_points === 'number' && !Number.isNaN(raw.score_points)
        ? Math.max(0, Math.min(100, Math.round(raw.score_points)))
        : 0,
    extraction_prompt:
      typeof raw.extraction_prompt === 'string' && raw.extraction_prompt.trim()
        ? raw.extraction_prompt.trim()
        : null,
    created_at: String(raw.created_at ?? ''),
    updated_at: String(raw.updated_at ?? ''),
  };
}

export function parseUi(raw: unknown): SubscriberFieldUi {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const o = raw as Record<string, unknown>;
  return {
    show_on_detail: o.show_on_detail === true,
    admin_only: o.admin_only === true,
    required: o.required === true,
    help_text: typeof o.help_text === 'string' ? o.help_text : undefined,
    sort_order: typeof o.sort_order === 'number' ? o.sort_order : undefined,
    placeholder: typeof o.placeholder === 'string' ? o.placeholder : undefined,
    admin_group:
      typeof o.admin_group === 'string' && o.admin_group.trim()
        ? o.admin_group.trim()
        : undefined,
    tags: parseFieldTags(o.tags),
    enable_upload: o.enable_upload === true,
    upload_accept:
      typeof o.upload_accept === 'string' && o.upload_accept.trim()
        ? o.upload_accept.trim()
        : undefined,
    upload_max_bytes:
      typeof o.upload_max_bytes === 'number' && o.upload_max_bytes > 0
        ? Math.min(Math.round(o.upload_max_bytes), CDN_UPLOAD_MAX_BYTES)
        : undefined,
    upload_cdn_key_template:
      typeof o.upload_cdn_key_template === 'string' && o.upload_cdn_key_template.trim()
        ? o.upload_cdn_key_template.trim()
        : undefined,
  };
}

export function fieldUploadEnabled(def: {
  field_type: SubscriberFieldType;
  ui?: SubscriberFieldUi;
}): boolean {
  return def.field_type === 'url' && def.ui?.enable_upload === true;
}

/** List view: show a thumbnail instead of the raw URL string. */
export function customFieldShowsImageInList(def: {
  key: string;
  field_type: SubscriberFieldType;
  ui?: SubscriberFieldUi;
}): boolean {
  if (def.field_type !== 'url') return false;
  return fieldUploadEnabled(def) || def.key === 'photo_url';
}

export function readCustomFieldUrl(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

export function fieldUploadAccept(ui?: SubscriberFieldUi): string {
  return ui?.upload_accept?.trim() || 'image/*';
}

export function fieldUploadMaxBytes(ui?: SubscriberFieldUi): number {
  const n = ui?.upload_max_bytes;
  if (typeof n === 'number' && n > 0) return Math.min(n, CDN_UPLOAD_MAX_BYTES);
  return CDN_UPLOAD_MAX_BYTES;
}

export function fieldUploadCdnKeyTemplate(ui?: SubscriberFieldUi): string {
  return ui?.upload_cdn_key_template?.trim() || 'subscribers/{subscriber_id}';
}

/** Default public CDN host (matches cdn-assets edge default). */
export const DEFAULT_CDN_PUBLIC_HOST = 'cdn.stephansmith.me';

/**
 * Hint for upload-enabled URL fields — derived from upload_cdn_key_template,
 * not legacy help_text that may say {id}.jpg.
 */
export function fieldUploadStorageHint(def: {
  field_type: SubscriberFieldType;
  ui?: SubscriberFieldUi;
}): string | undefined {
  if (!fieldUploadEnabled(def)) return undefined;
  const template = fieldUploadCdnKeyTemplate(def.ui);
  return `Upload saves to ${DEFAULT_CDN_PUBLIC_HOST}/${template}.{ext} — the public URL is stored in this field.`;
}

const MIME_TO_EXT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

function extFromFile(file: File): string {
  const fromMime = MIME_TO_EXT[file.type];
  if (fromMime) return fromMime;
  const fromName = file.name.split('.').pop()?.toLowerCase();
  if (fromName && /^[a-z0-9]{1,8}$/.test(fromName)) return fromName;
  return 'jpg';
}

export function buildFieldUploadCdnKey(
  template: string,
  subscriberId: string,
  file: File,
): string {
  const ext = extFromFile(file);
  const base = template
    .trim()
    .replace(/\{subscriber_id\}/g, subscriberId)
    .replace(/\/+$/, '')
    .replace(/\.[a-z0-9]+$/i, '');
  return `${base}.${ext}`;
}

export function validateFieldUploadFile(file: File, ui?: SubscriberFieldUi): string | null {
  const max = fieldUploadMaxBytes(ui);
  if (file.size > max) {
    const mb = max / (1024 * 1024);
    return `File exceeds ${mb % 1 === 0 ? mb : mb.toFixed(1)} MB limit`;
  }

  const accept = fieldUploadAccept(ui);
  if (accept === 'image/*') {
    return file.type.startsWith('image/') ? null : 'Choose an image file';
  }

  const parts = accept.split(',').map((s) => s.trim()).filter(Boolean);
  if (!parts.length) return null;

  const ok = parts.some((part) => {
    if (part.endsWith('/*')) return file.type.startsWith(part.slice(0, -1));
    return file.type === part;
  });
  return ok ? null : 'File type not allowed';
}

export function isLikelyImageUrl(url: string): boolean {
  const t = url.trim();
  if (!t) return false;
  return /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(t) || t.includes('/subscribers/');
}

export function adminGroupLabel(def: { ui?: SubscriberFieldUi }): string {
  return def.ui?.admin_group?.trim() || DEFAULT_ADMIN_FIELD_GROUP;
}

export function groupDefinitionsByAdminGroup<T extends { ui?: SubscriberFieldUi; label: string }>(
  defs: T[],
): { group: string; items: T[] }[] {
  const map = new Map<string, T[]>();
  for (const def of sortDefinitions(defs)) {
    const group = adminGroupLabel(def);
    const bucket = map.get(group);
    if (bucket) bucket.push(def);
    else map.set(group, [def]);
  }

  return [...map.entries()]
    .sort(([a], [b]) => {
      if (a === DEFAULT_ADMIN_FIELD_GROUP) return 1;
      if (b === DEFAULT_ADMIN_FIELD_GROUP) return -1;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    })
    .map(([group, items]) => ({ group, items }));
}

export function sortDefinitions<T extends { ui?: SubscriberFieldUi; label: string }>(
  defs: T[],
): T[] {
  return [...defs].sort((a, b) => {
    const ao = a.ui?.sort_order ?? 9999;
    const bo = b.ui?.sort_order ?? 9999;
    if (ao !== bo) return ao - bo;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });
}

const LINKEDIN_CUSTOM_FIELD_KEYS = new Set([
  'linkedin',
  'linkedin_url',
  'linked_url',
  'linkedinurl',
]);

/** True when a custom field definition stores a LinkedIn profile URL. */
export function isLinkedInUrlCustomField(field: {
  key: string;
  label: string;
  field_type?: SubscriberFieldType;
  fieldType?: SubscriberFieldType;
}): boolean {
  const key = field.key.trim().toLowerCase();
  if (LINKEDIN_CUSTOM_FIELD_KEYS.has(key)) return true;

  const label = field.label.trim().toLowerCase();
  if (label === 'linkedin' || label === 'linkedin url' || label === 'linked in url') return true;
  if (label.includes('linkedin') && (label.includes('url') || label.includes('profile'))) {
    return true;
  }

  const type = field.field_type ?? field.fieldType;
  return type === 'url' && label.includes('linkedin');
}

export function externalUrlHref(url: string): string {
  const t = url.trim();
  if (!t) return '#';
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

/** Resolve stored LinkedIn slug or partial URL to a browser-openable profile URL. */
export function linkedinCustomFieldHref(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/linkedin\.com/i.test(trimmed)) {
    return trimmed.startsWith('http') ? trimmed : `https://${trimmed.replace(/^\/+/, '')}`;
  }
  const pathy = trimmed.replace(/^\/+/, '');
  if (/^in\//i.test(pathy) || pathy.includes('/')) {
    return `https://www.linkedin.com/${pathy}`;
  }
  if (/^[a-zA-Z0-9_-]+$/.test(pathy)) {
    return `https://www.linkedin.com/in/${pathy}`;
  }
  return externalUrlHref(trimmed);
}

/** Strip HTML for native `<select>` labels and list cells (values stay full HTML in DB). */
export function stripHtmlTags(value: string): string {
  return String(value)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** Plain label for a select option (option `value` keeps the stored string with HTML). */
export function optionDisplayLabel(raw: string): string {
  return stripHtmlTags(raw);
}

/** Read-only cell text for custom field values that may contain HTML markup. */
export function formatCustomFieldDisplayValue(
  value: unknown,
  fieldType: SubscriberFieldType,
): string {
  const draft = valueToDraft(value, fieldType).trim();
  if (!draft) return '';
  if (fieldType === 'multi_select') {
    return draft
      .split(',')
      .map((part) => optionDisplayLabel(part.trim()))
      .filter(Boolean)
      .join(', ');
  }
  if (fieldType === 'select') return optionDisplayLabel(draft);
  return draft;
}

export function valueToDraft(value: unknown, fieldType: SubscriberFieldType): string {
  if (value === null || value === undefined) return '';
  if (fieldType === 'boolean') {
    if (value === true) return 'true';
    if (value === false) return 'false';
    return '';
  }
  if (fieldType === 'multi_select' && Array.isArray(value)) {
    return value.map((v) => String(v)).join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export type ParseFieldResult = { ok: true; value: unknown } | { ok: false; error: string };

export function parseDraftForSave(
  raw: string,
  fieldType: SubscriberFieldType,
  options?: string[],
): ParseFieldResult {
  const t = raw.trim();
  if (t === '') return { ok: true, value: null };

  switch (fieldType) {
    case 'boolean': {
      if (/^(true|1|yes)$/i.test(t)) return { ok: true, value: true };
      if (/^(false|0|no)$/i.test(t)) return { ok: true, value: false };
      return { ok: false, error: 'Use true or false' };
    }
    case 'number': {
      const n = Number(t);
      if (Number.isNaN(n)) return { ok: false, error: 'Invalid number' };
      return { ok: true, value: n };
    }
    case 'multi_select': {
      const parts = t.split(',').map((s) => s.trim()).filter(Boolean);
      if (options?.length) {
        for (const p of parts) {
          if (!options.includes(p)) {
            return { ok: false, error: `Invalid option: ${p}` };
          }
        }
      }
      return { ok: true, value: parts };
    }
    case 'select': {
      if (options?.length && t && !options.includes(t)) {
        return { ok: false, error: 'Pick one of the allowed options' };
      }
      return { ok: true, value: t };
    }
    default:
      return { ok: true, value: t };
  }
}

/** Flatten custom_data values to strings for email template substitution. */
export function customDataToTemplateVars(customData: CustomDataMap | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!customData || typeof customData !== 'object') return out;
  for (const [key, val] of Object.entries(customData)) {
    if (val === null || val === undefined) {
      out[key] = '';
    } else if (typeof val === 'boolean') {
      out[key] = val ? 'true' : 'false';
    } else if (Array.isArray(val)) {
      out[key] = val.map((v) => String(v)).join(', ');
    } else if (typeof val === 'object') {
      out[key] = JSON.stringify(val);
    } else {
      out[key] = String(val);
    }
  }
  return out;
}
